import api from '../api/client';

let swRegistration: ServiceWorkerRegistration | null = null;

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const swPath = (import.meta.env.BASE_URL || '/') + 'service-worker.js';
    swRegistration = await navigator.serviceWorker.register(swPath);
    return swRegistration;
  } catch (err) {
    console.error('[Push] Service worker registration failed:', err);
    return null;
  }
}

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return swRegistration;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  return Notification.requestPermission();
}

export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await api.get<{ data?: { public_key: string } }>('/api/v1/push-subscriptions/vapid-public-key');
    return res.data.data?.public_key || null;
  } catch (err) {
    console.error('[Push] VAPID public key alınamadı:', err);
    return null;
  }
}

export async function subscribeToPushNotifications(): Promise<boolean> {
  if (!isPushSupported() || !swRegistration) {
    return false;
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return false;
  }

  try {
    let subscription = await swRegistration.pushManager.getSubscription();

    // Mevcut subscription yoksa veya key bilgileri eksikse yeniden abone ol.
    if (
      subscription &&
      subscription.getKey('p256dh') &&
      subscription.getKey('auth')
    ) {
      await sendSubscriptionToServer(subscription);
      return true;
    }

    const publicKey = await getVapidPublicKey();
    if (!publicKey) {
      return false;
    }

    subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });

    await sendSubscriptionToServer(subscription);
    return true;
  } catch (err) {
    console.error('[Push] Subscription failed:', err);
    return false;
  }
}

/**
 * Mevcut push subscription'ını backend ile senkronize et.
 * Sayfa yenilendiğinde veya service worker pushsubscriptionchange event'inden
 * sonra çağrılabilir.
 */
export async function syncPushSubscription(): Promise<boolean> {
  if (!isPushSupported() || !swRegistration) {
    return false;
  }

  if (Notification.permission !== 'granted') {
    return false;
  }

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (
      subscription &&
      subscription.getKey('p256dh') &&
      subscription.getKey('auth')
    ) {
      await sendSubscriptionToServer(subscription);
      return true;
    }
    return false;
  } catch (err) {
    console.error('[Push] Sync subscription failed:', err);
    return false;
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  if (!swRegistration) {
    return false;
  }

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (!subscription) {
      return true;
    }

    await api.delete('/api/v1/push-subscriptions', {
      data: { endpoint: subscription.endpoint },
    });

    await subscription.unsubscribe();
    return true;
  } catch (err) {
    console.error('[Push] Unsubscribe failed:', err);
    return false;
  }
}

async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  await api.post('/api/v1/push-subscriptions', {
    endpoint: subscription.endpoint,
    public_key: key ? btoa(String.fromCharCode(...new Uint8Array(key))) : '',
    auth_token: auth ? btoa(String.fromCharCode(...new Uint8Array(auth))) : '',
    p256dh: key ? btoa(String.fromCharCode(...new Uint8Array(key))) : '',
  });
}

export async function showLocalNotification(
  title: string,
  options?: NotificationOptions
): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    if (swRegistration?.active) {
      await swRegistration.showNotification(title, options);
    } else {
      // Fallback: service worker hazır değilse Notification API üzerinden göster
      new Notification(title, options);
    }
  } catch (err) {
    console.error('[Push] Local notification failed:', err);
  }
}
